import { useState, useRef, useEffect } from 'react';
import { Palette, X } from 'lucide-react';

const PRESET_COLORS = [
  // Brand colors
  '#1E5BB5', // Royal Blue
  '#00B359', // Green
  '#FFA726', // Orange
  '#1F2937', // Dark Gray
  '#6B7280', // Light Gray
  
  // Standard colors
  '#000000', // Black
  '#FFFFFF', // White
  '#EF4444', // Red
  '#F59E0B', // Amber
  '#10B981', // Emerald
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  
  // Grays
  '#F9FAFB', // Very Light Gray
  '#F3F4F6', // Light Gray
  '#E5E7EB', // Gray
  '#D1D5DB', // Medium Gray
  '#9CA3AF', // Dark Gray
  '#4B5563', // Very Dark Gray
];

export default function ColorPalette({ 
  isOpen, 
  onClose, 
  onSelectColor,
  currentColor = '#1E5BB5',
  position = { x: 0, y: 0 }
}) {
  const [customColor, setCustomColor] = useState(currentColor);
  const paletteRef = useRef(null);

  useEffect(() => {
    if (isOpen && paletteRef.current) {
      paletteRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (paletteRef.current && !paletteRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleColorSelect = (color) => {
    onSelectColor(color);
    onClose();
  };

  const handleCustomColorChange = (e) => {
    const color = e.target.value;
    setCustomColor(color);
    onSelectColor(color);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={paletteRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        minWidth: '280px',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Palette size={16} className="text-gray-600" />
          <span className="text-sm font-semibold text-gray-700">Color Palette</span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <X size={16} />
        </button>
      </div>

      <div className="mb-3">
        <label className="text-xs text-gray-600 mb-1 block">Preset Colors</label>
        <div className="grid grid-cols-8 gap-2">
          {PRESET_COLORS.map((color) => (
            <button
              key={color}
              onClick={() => handleColorSelect(color)}
              className={`
                w-8 h-8 rounded border-2 transition-all
                ${currentColor === color ? 'border-blue-500 scale-110' : 'border-gray-300 hover:border-gray-400'}
              `}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-600 mb-1 block">Custom Color</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={customColor}
            onChange={handleCustomColorChange}
            className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
          />
          <input
            type="text"
            value={customColor}
            onChange={(e) => {
              const color = e.target.value;
              setCustomColor(color);
              if (/^#[0-9A-F]{6}$/i.test(color)) {
                onSelectColor(color);
              }
            }}
            placeholder="#1E5BB5"
            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200">
        <div className="text-xs text-gray-500">
          Current: <span className="font-mono">{currentColor}</span>
        </div>
      </div>
    </div>
  );
}

